import { useCallback } from "react";
import { useSelector } from "react-redux";

/**
 * Data hook for the Dev Guide screen. Talks to the S3-backed Lambda behind
 * REACT_APP_DEV_GUIDE_API, which organizes content as folders (sections) of
 * uploaded Markdown documents.
 *
 * The endpoint sits behind the same API Gateway authorizer as the rest of the
 * app, so every call carries the logged-in user's Authorization token. Mirrors
 * the request/response conventions of useSubContractingData (POST + RequestType,
 * with the Lambda proxy body optionally returned as a JSON string).
 *
 * Contract:
 *   POST { RequestType: "listDevGuideSections" }
 *     → { sections: [{ id, label }] }
 *   POST { RequestType: "listDevGuideDocuments", section }
 *     → { documents: [{ id, title, key, updated }] }
 *   POST { RequestType: "getDevGuideDocument", key }
 *     → { title, updated, content: "<raw markdown>" }
 */
const API_URL = process.env.REACT_APP_DEV_GUIDE_API;

const useDevGuideData = () => {
    const user = useSelector((store) => store?.user);
    const authToken = user?.$;

    /**
     * POST a body to the Dev Guide Lambda and return the parsed payload.
     * Unwraps the API Gateway proxy shape (body as a JSON string) and throws on
     * any non-2xx or an explicit `{ success: false }` failure body.
     *
     * @param {object} body - Request payload (must include a RequestType).
     * @returns {Promise<object>} The parsed response payload.
     */
    const post = useCallback(
        async (body) => {
            // Guard against a missing env var (e.g. the dev server wasn't
            // restarted after adding REACT_APP_DEV_GUIDE_API) — otherwise fetch
            // hits a bad URL and returns the app's HTML shell, which then fails
            // JSON parsing with a confusing "Unexpected token '<'" error.
            if (!API_URL) {
                throw new Error(
                    "Dev Guide API URL is not set. Add REACT_APP_DEV_GUIDE_API to .env and restart the dev server."
                );
            }

            const res = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(authToken && { Authorization: authToken }),
                },
                body: JSON.stringify(body),
            });

            // Read as text first so we can give a useful error when the response
            // is HTML (an error page, redirect, or the SPA shell) rather than
            // JSON. res.json() on HTML throws the cryptic "Unexpected token '<'".
            const raw = await res.text();
            let json;
            try {
                json = JSON.parse(raw);
            } catch {
                const looksHtml = raw.trimStart().startsWith("<");
                throw new Error(
                    looksHtml
                        ? `Expected JSON but received an HTML response (HTTP ${res.status}). ` +
                          "The request likely didn't reach the Lambda — check the API URL and that you're authenticated."
                        : `Invalid response (HTTP ${res.status}): ${raw.slice(0, 120)}`
                );
            }

            const parsed = typeof json.body === "string" ? JSON.parse(json.body) : json;
            if (!res.ok || parsed?.success === false) {
                throw new Error(
                    parsed?.error || parsed?.message || `Request failed (HTTP ${res.status})`
                );
            }
            return parsed;
        },
        [authToken]
    );

    /**
     * List the top-level folders (e.g. Guidelines, Learnings).
     * @returns {Promise<Array<{id: string, label: string}>>}
     */
    const listSections = useCallback(async () => {
        const result = await post({ RequestType: "listDevGuideSections" });
        return result.sections || [];
    }, [post]);

    /**
     * List the Markdown documents within a section (folder).
     * @param {string} section - The section id (folder name).
     * @returns {Promise<Array<{id, title, key, updated}>>}
     */
    const listDocuments = useCallback(
        async (section) => {
            const result = await post({ RequestType: "listDevGuideDocuments", section });
            return result.documents || [];
        },
        [post]
    );

    /**
     * Fetch a single document's Markdown content by its S3 key.
     * @param {string} key - The object key, e.g. "Guidelines/django.md".
     * @returns {Promise<{title, updated, content}>}
     */
    const getDocument = useCallback(
        async (key) => {
            const result = await post({ RequestType: "getDevGuideDocument", key });
            return {
                title: result.title || "",
                updated: result.updated || "",
                content: result.content || "",
            };
        },
        [post]
    );

    return { listSections, listDocuments, getDocument };
};

export default useDevGuideData;
